import {
  UseFormReturn,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors
} from 'react-hook-form';
import { Typography } from '@navi/web-ui/lib/primitives';
import { ConfigurationFormData } from '../../types';
import RequiredIcon from '@navi/web-ui/lib/icons/RequiredIcon';
import DPDExclusionField from './DPDExclusionField';
import DateField from './DateField';
import DropdownField from './DropdownField';
import TextInputField from './TextInputField';
import {
  getFieldName,
  getFieldType,
  FieldType,
  getMaxValue,
  getPlaceholder,
  showTooltipInfo,
  transformOptions,
  isMultiSelectDropdown
} from './utils';
import DPDTooltipContent from './DPDTooltipContent';
import styles from '../index.module.scss';
import cx from 'classnames';
import { FormQuestion, FormSection } from './interface';

interface FormFieldsRendererProps {
  configurationSections: FormSection[] | null;
  control: UseFormReturn<ConfigurationFormData>['control'];
  errors: UseFormReturn<ConfigurationFormData>['formState']['errors'];
  configurationData?: ConfigurationFormData;
  isDrawerOpen?: boolean;
  setValue: UseFormSetValue<ConfigurationFormData>;
  setError: UseFormSetError<ConfigurationFormData>;
  clearErrors: UseFormClearErrors<ConfigurationFormData>;
}

const FormFieldsRenderer = ({
  configurationSections,
  control,
  errors,
  configurationData,
  isDrawerOpen,
  setValue,
  setError,
  clearErrors
}: FormFieldsRendererProps) => {
  if (!configurationSections || configurationSections.length === 0) {
    return null;
  }

  return (
    <>
      {configurationSections.map((section: FormSection) => (
        <div key={section.id} className={styles.formSection}>
          <Typography variant="h5" className="mb-4">
            {section?.label}
          </Typography>

          {section.questions?.map((question: FormQuestion) => {
            const fieldName = getFieldName(question.id);
            if (!fieldName) return null;

            const fieldType = getFieldType(question.id, question.options);

            const renderFieldByType = () => {
              switch (fieldType) {
                case FieldType.DPD_EXCLUSION:
                  return (
                    <DPDExclusionField
                      configurationData={configurationData}
                      isDrawerOpen={isDrawerOpen}
                      setValue={setValue}
                      setError={setError}
                      clearErrors={clearErrors}
                    />
                  );

                case FieldType.DATE:
                  return (
                    <DateField
                      control={control}
                      fieldName={fieldName}
                      label={question.label}
                      mandatory={question.mandatory}
                    />
                  );

                case FieldType.DROPDOWN:
                  return (
                    <DropdownField
                      control={control}
                      errors={errors}
                      fieldName={fieldName}
                      label={question.label}
                      mandatory={question.mandatory}
                      options={question.options}
                      isMultiSelect={isMultiSelectDropdown(question.id)}
                      transformedOptions={transformOptions(question.options)}
                      placeholder={getPlaceholder(question.id, false)}
                    />
                  );

                case FieldType.TEXT_INPUT:
                  return (
                    <TextInputField
                      control={control}
                      errors={errors}
                      fieldName={fieldName}
                      label={question.label}
                      mandatory={question.mandatory}
                      maxValue={getMaxValue(question.id)}
                      placeholder={getPlaceholder(question.id, true)}
                    />
                  );

                default:
                  return null;
              }
            };

            return (
              <div key={question.id} className="mb-4">
                <div className={cx(styles.textContainer, 'pb-1')}>
                  <Typography variant="p4" color="var(--navi-color-gray-c2)">
                    {question.label}
                  </Typography>
                  {question.mandatory && <RequiredIcon />}

                  {showTooltipInfo(fieldType) && <DPDTooltipContent />}
                </div>
                {renderFieldByType()}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

export default FormFieldsRenderer;
